

import { createContext, useCallback, useContext, useState, useMemo } from "react";
import { getEmailSent } from "@/lib/api/handlers/EmailSentHandling";
import { SENT_MAIL_DETAIL_FIELDS } from "@/lib/tables/sentMailsFields";
import { EmailSentDTOStatistics } from "../types/EmailSentDTOStatistics";

type SentMailsDetailsContextType = {
  data: Map<string, EmailSentDTOStatistics>;
  loading: boolean;
  error: string | null;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setData: React.Dispatch<
    React.SetStateAction<Map<string, EmailSentDTOStatistics>>
  >;
  getSentMailDetails: (sentEmailId: string) => Promise<EmailSentDTOStatistics>;
};

const SentMailsDetailsContext = createContext<
  SentMailsDetailsContextType | undefined
>(undefined);

export const SentMailsDetailsProvider = ({
  children,
  eventId,
}: {
  children: React.ReactNode;
  eventId: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Map<string, EmailSentDTOStatistics>>(
    new Map(),
  );

  const getSentMailDetails = useCallback(
    async (sentEmailId: string) => {
      const relevantData = data.get(sentEmailId);

      if (!relevantData) {
        setLoading(true);
        setError(null);

        try {
          const emailSentContent = await getEmailSent(
            eventId,
            sentEmailId,
            SENT_MAIL_DETAIL_FIELDS,
          );

          if (
            typeof emailSentContent === "object" &&
            emailSentContent !== null &&
            "isServerError" in emailSentContent &&
            emailSentContent.isServerError === true
          ) {
            throw emailSentContent.message;
          }

          setData((prevData) => {
            const newData = new Map(prevData);
            // s3Content is the HTML string result here — cast to EmailSentDTO to match the Map value type
            newData.set(
              sentEmailId,
              emailSentContent as unknown as EmailSentDTOStatistics,
            );
            return newData;
          });

          return emailSentContent as EmailSentDTOStatistics;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to fetch email content";
          setError(errorMessage);
          throw err;
        } finally {
          setLoading(false);
        }
      }

      return relevantData;
    },
    [data, eventId, setLoading, setError, setData],
  );

  const contextValue = useMemo(() => ({
    loading, error, setError, setLoading, data, setData, getSentMailDetails,
  }), [loading, error, data, getSentMailDetails]);

  return (
    <SentMailsDetailsContext.Provider
      value={contextValue}
    >
      {children}
    </SentMailsDetailsContext.Provider>
  );
};

export const useSentMailsDetails = () => {
  const context = useContext(SentMailsDetailsContext);
  if (!context) {
    throw new Error(
      "useSentMailsDetails must be used within a SentMailsDetailsProvider",
    );
  }
  return context;
};
